<template>
  <div :class="['zs-checkbox-group', `layout-${layout}`]">
    <ZSCheckBox
      v-for="(option, index) in normalizedOptions"
      :key="index"
      :label="option.label"
      :value="option.value"
      :size="size"
      :alignment="alignment"
      :labelPosition="labelPosition"
      :disabled="disabled || option.disabled"
    />
    <slot />
  </div>
</template>

<script setup>
import { computed, provide, toRef } from 'vue'
import ZSCheckBox from './ZSCheckBox.vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  layout: {
    type: String,
    default: 'vertical',
    validator: (value) => ['vertical', 'horizontal'].includes(value)
  },
  // Pass-through props for ZSCheckBox
  size: {
    type: String,
    default: 'default'
  },
  alignment: {
    type: String,
    default: 'middle'
  },
  labelPosition: {
    type: String,
    default: 'right'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const onCheckboxChange = (isChecked, value) => {
  const newValue = [...(props.modelValue || [])]
  
  if (isChecked) {
    if (!newValue.includes(value)) {
      newValue.push(value)
    }
  } else {
    const index = newValue.indexOf(value)
    if (index > -1) {
      newValue.splice(index, 1)
    }
  }
  
  emit('update:modelValue', newValue)
}

provide('checkboxGroupModel', computed(() => props.modelValue))
provide('checkboxGroupChange', onCheckboxChange)
provide('checkboxGroupSize', toRef(props, 'size'))
provide('checkboxGroupAlignment', toRef(props, 'alignment'))
provide('checkboxGroupLabelPosition', toRef(props, 'labelPosition'))
provide('checkboxGroupDisabled', toRef(props, 'disabled'))

/**
 * Normalizes options to always be an array of objects { label, value }
 */
const normalizedOptions = computed(() => {
  return props.options.map(option => {
    if (typeof option === 'object' && option !== null) {
      return {
        label: option[props.labelKey],
        value: option[props.valueKey],
        disabled: option.disabled || false
      }
    }
    // Handle plain string/number arrays
    return {
      label: String(option),
      value: option,
      disabled: false
    }
  })
})
</script>

<style scoped>
.zs-checkbox-group {
  display: flex;
  gap: 10px;
}

.zs-checkbox-group.layout-vertical {
  flex-direction: column;
}

.zs-checkbox-group.layout-horizontal {
  flex-direction: row;
  flex-wrap: wrap;
}
</style>
